/**
 * Sticky Add-to-Cart Component
 * 
 * Advanced sticky add-to-cart bar that appears when main product form scrolls out of view.
 * Features full bidirectional sync with main form including variants, quantities, and pricing.
 * 
 * Key Features:
 * - Bidirectional variant sync (updates URL and main form when changed)
 * - Bidirectional quantity sync with main product form
 * - Real-time price updates (regular, sale, and compare-at prices)
 * - Dynamic variant selector width based on selected option text
 * - Caret icon animation on focus
 * - Click-outside blur handling
 * - Integration with theme's variant-radios custom element
 * - Supports Standard (full-width) and Minimal (floating) display styles
 * 
 * Architecture:
 * - Uses IntersectionObserver for scroll-based visibility
 * - Leverages theme's PUB_SUB_EVENTS system for variant changes
 * - Works with theme's variant-radios custom element (located outside product form)
 * - Prevents sync loops with isUpdating flag
 * 
 * Author: Alec Hewstone (Kubix Media)
 * Created: 13/11/2025
 * Last Updated: 14/11/2025
 */

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          isOpen: 'sticky-atc-bar--show',
          buttonLoading: 'loading',
        };

        this.config = {
          productId: this.dataset.productId,
          sectionId: this.dataset.sectionId,
          mainFormSelector: this.dataset.mainFormSelector || '#product-form-' + this.dataset.sectionId,
          style: this.dataset.style || 'standard',
        };
      }

      get submitButton() {
        return (this._submitButton = this._submitButton || this.querySelector('[type="submit"]'));
      }

      get quantityInput() {
        return (this._quantityInput = this._quantityInput || this.querySelector('[data-sticky-quantity]'));
      }

      get variantSelect() {
        return (this._variantSelect = this._variantSelect || this.querySelector('[data-sticky-variant]'));
      }

      get variantIdSelect() {
        return (this._variantIdSelect = this._variantIdSelect || this.querySelector('[name="id"]'));
      }

      get stickyBarWrapper() {
        return this.config.style === 'minimal' ? this : this.closest('.sticky-atc-bar');
      }

      getVariantData() {
        if (!this.variantData) {
          const jsonScript = this.querySelector('[type="application/json"]');
          if (jsonScript) {
            this.variantData = JSON.parse(jsonScript.textContent);
          }
        }
        return this.variantData;
      }

      connectedCallback() {
        this.productForm = document.querySelector(this.config.mainFormSelector);
        
        if (!this.productForm) return;

        if (this.config.style === 'standard') {
          this.mainQuantityInput = this.productForm.querySelector('[name="quantity"]');
        }
        
        this.init();
        this.setupEventListeners();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.cartErrorUnsubscriber) this.cartErrorUnsubscriber();
        if (this.measureSpan) {
          this.measureSpan.remove();
        }
        if (this.clickOutsideHandler) {
          document.removeEventListener('mousedown', this.clickOutsideHandler);
        }
        // Reset chat button position when component is removed
        this.adjustChatButtonPosition(false);
      }

      init() {
        const rootMargin = `${this.productForm.offsetHeight}px 0px 0px 0px`;
        
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const isAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              const wrapper = this.stickyBarWrapper;
              if (wrapper) {
                wrapper.classList.toggle(this.classes.isOpen, isAboveViewport);
                this.adjustChatButtonPosition(isAboveViewport);
              }
            });
          },
          { threshold: 1, rootMargin }
        );

        this.observer.observe(this.productForm);
        this.syncWithMainProductForm();
        
        if (this.config.style === 'standard') {
          this.setupQuantitySync();
        }
        this.setupVariantSync();
      }

      setupEventListeners() {
        if (!this.submitButton) return;

        this.submitButton.addEventListener('click', this.onButtonClick.bind(this));

        if (typeof subscribe === 'function') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
            this.resetButtonLoading();
          });

          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => {
            this.resetButtonLoading();
            this.scrollToMainForm();
          });
        }
      }

      setupQuantitySync() {
        if (!this.quantityInput || !this.mainQuantityInput) return;

        this.quantityInput.value = this.mainQuantityInput.value || 1;

        this.mainQuantityInput.addEventListener('change', () => {
          this.quantityInput.value = this.mainQuantityInput.value;
        });

        this.quantityInput.addEventListener('change', () => {
          this.mainQuantityInput.value = this.quantityInput.value;
          this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        });

        this.quantityInput.addEventListener('input', () => {
          this.mainQuantityInput.value = this.quantityInput.value;
        });
      }

      setupVariantSync() {
        if (!this.variantSelect || !this.variantIdSelect) return;

        // Set initial width on load (before any early returns)
        this.adjustVariantSelectWidth();

        // Make caret icon clickable (before any early returns)
        const caretIcon = this.querySelector('.sticky-atc-bar__variant-selector .select svg');
        
        if (caretIcon) {
          caretIcon.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Focus the select first
            this.variantSelect.focus();
            
            // Try modern showPicker() API first (Chrome, Edge, Safari 16+)
            if (typeof this.variantSelect.showPicker === 'function') {
              try {
                this.variantSelect.showPicker();
              } catch (error) {
                // Fallback: dispatch mousedown and click events
                const mouseDownEvent = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                });
                const clickEvent = new MouseEvent('click', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                });
                
                this.variantSelect.dispatchEvent(mouseDownEvent);
                this.variantSelect.dispatchEvent(clickEvent);
              }
            } else {
              // Fallback: dispatch mousedown and click events
              const mouseDownEvent = new MouseEvent('mousedown', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              
              this.variantSelect.dispatchEvent(mouseDownEvent);
              this.variantSelect.dispatchEvent(clickEvent);
            }
          });
        }

        // Get variant data before setting up handlers (needed for sync)
        this.variantData = this.getVariantData();
        
        // Listen to sticky bar variant changes (before any early returns)
        this.variantSelect.addEventListener('change', () => {
          // Set aria-busy for screen readers
          this.variantSelect.setAttribute('aria-busy', 'true');
          
          // 1. Width adjustment
          this.variantSelect.style.transition = 'none';
          this.adjustVariantSelectWidth();
          requestAnimationFrame(() => {
            this.variantSelect.style.transition = '';
          });
          
          // 2. Get selected variant IMMEDIATELY (before any early returns)
          const selectedVariantId = parseInt(this.variantIdSelect.value, 10);
          this.currentVariant = this.variantData?.find((variant) => variant.id === selectedVariantId);
          
          // 3. Update sticky bar price/button IMMEDIATELY (independent of main form sync)
          if (this.currentVariant) {
            this.updatePrice(this.currentVariant);
            this.updateButton(this.currentVariant);
            this.variantSelect.removeAttribute('aria-busy');
          }
          
          // 4. Blur handling
          setTimeout(() => {
            this.variantSelect.blur();
          }, 100);
          
          // 5. Main form sync - works with variant-radios custom element
          if (!this.variantData) return;

          if (this.isUpdating) return;
          this.isUpdating = true;

          if (this.currentVariant) {
            // Find variant-radios element (it's a sibling of product-form, not inside it!)
            const variantRadios = document.querySelector(`variant-radios[data-section="${this.config.sectionId}"]`);
            
            if (variantRadios) {
              // Find and check radio buttons for each option
              this.currentVariant.options.forEach((optionValue) => {
                // Find radio button inside variant-radios element
                const radioButton = variantRadios.querySelector(`input[type="radio"][value="${optionValue}"]`);
                
                if (radioButton) {
                  radioButton.checked = true;
                }
              });
              
              // Trigger change event on variant-radios element itself
              // This will make it handle URL update, publish event, etc.
              setTimeout(() => {
                try {
                  variantRadios.dispatchEvent(new Event('change', { bubbles: true }));
                  this.isUpdating = false;
                } catch (error) {
                  console.error('Sticky ATC: Failed to sync variant to main form', error);
                  this.isUpdating = false;
                }
              }, 0);
            } else {
              this.isUpdating = false;
            }
          } else {
            this.isUpdating = false;
          }
        });

        // Handle clicks outside to blur the select
        this.clickOutsideHandler = this.handleClickOutside.bind(this);
        document.addEventListener('mousedown', this.clickOutsideHandler);
      }

      adjustVariantSelectWidth() {
        if (!this.variantSelect) return;

        const selectedText = this.variantSelect.options[this.variantSelect.selectedIndex]?.text || '';
        if (!selectedText) return;

        // Create hidden span to measure text
        if (!this.measureSpan) {
          this.measureSpan = document.createElement('span');
          this.measureSpan.style.cssText = 'position: absolute; visibility: hidden; white-space: nowrap;';
          document.body.appendChild(this.measureSpan);
        }

        // Copy font styles from select
        const selectStyles = getComputedStyle(this.variantSelect);
        this.measureSpan.style.fontSize = selectStyles.fontSize;
        this.measureSpan.style.fontFamily = selectStyles.fontFamily;
        this.measureSpan.style.fontWeight = selectStyles.fontWeight;
        this.measureSpan.textContent = selectedText;

        // Calculate width: text width + caret space
        const textWidth = this.measureSpan.offsetWidth;
        const caretSpace = 72; // Space for icon + padding
        const minWidth = 150; // Minimum 150px

        const calculatedWidth = Math.max(textWidth + caretSpace, minWidth);
        
        // Set width dynamically based on content
        this.variantSelect.style.width = `${calculatedWidth}px`;
      }

      handleClickOutside(event) {
        if (!this.variantSelect) return;

        // If clicking outside the select wrapper
        const selectWrapper = this.variantSelect.closest('.sticky-atc-bar__variant-selector');
        if (selectWrapper && !selectWrapper.contains(event.target)) {
          // Blur the select if it's focused
          if (document.activeElement === this.variantSelect) {
            this.variantSelect.blur();
          }
        }
      }

      onButtonClick() {
        if (this.config.style === 'standard' && this.quantityInput && this.mainQuantityInput) {
          this.mainQuantityInput.value = this.quantityInput.value;
        }

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add(this.classes.buttonLoading);
        
        const spinner = this.submitButton.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }

      resetButtonLoading() {
        if (!this.submitButton) return;

        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.classList.remove(this.classes.buttonLoading);
        
        const spinner = this.submitButton.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.add('hidden');
      }

      scrollToMainForm(offset = 200) {
        if (!this.productForm) return;

        window.scrollTo({
          behavior: 'smooth',
          top: this.productForm.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
        });
      }

      adjustChatButtonPosition(isVisible) {
        const chatButton = document.getElementById('chat-button');
        if (!chatButton) return;

        // Calculate appropriate bottom offset based on viewport width
        const isMobile = window.innerWidth <= 749;
        const bottomOffset = isVisible ? (isMobile ? '7.5rem' : '9rem') : '';

        chatButton.style.bottom = bottomOffset;
      }

      updateButton(variant) {
        if (!this.submitButton) return;

        const buttonText = this.submitButton.querySelector('span');
        if (!buttonText) return;

        if (!variant || !variant.available) {
          this.submitButton.setAttribute('disabled', 'disabled');
          buttonText.textContent = window.variantStrings?.soldOut || 'Sold out';
        } else {
          this.submitButton.removeAttribute('disabled');
          buttonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        }
      }

      updatePrice(variant) {
        if (!variant) return;

        const priceWrapper = this.querySelector('.price');
        if (!priceWrapper) return;

        const { compare_at_price, price, available } = variant;

        priceWrapper.classList.toggle('price--on-sale', compare_at_price && compare_at_price > price);
        priceWrapper.classList.toggle('price--sold-out', !available);

        const moneyFormat = window.theme?.moneyFormat || '${{amount}}';
        const formatMoney = (cents) => {
          if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
            return Shopify.formatMoney(cents, moneyFormat);
          }
          return moneyFormat.replace('{{amount}}', (cents / 100).toFixed(2));
        };

        const regularPrice = priceWrapper.querySelector('.price__regular .price-item--regular');
        if (regularPrice) regularPrice.textContent = formatMoney(price);

        const salePrice = priceWrapper.querySelector('.price__sale .price-item--sale');
        if (salePrice) salePrice.textContent = formatMoney(price);

        const comparePrice = priceWrapper.querySelector('.price__compare .price-item--regular');
        if (comparePrice && compare_at_price && compare_at_price > price) {
          comparePrice.textContent = formatMoney(compare_at_price);
        }
      }

      syncWithMainProductForm() {
        if (typeof subscribe !== 'function') return;
        if (typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          // Defensive check for event structure
          if (!event) return;
          
          if (event.sectionId !== this.config.sectionId) return;

          const variant = event.variant;
          
          // Update variant selector
          if (this.variantIdSelect && variant) {
            this.currentVariant = variant;
            this.variantIdSelect.value = variant.id;
            this.adjustVariantSelectWidth();
          }
          
          this.updatePrice(variant);
          this.updateButton(variant);
        });
      }
    }
  );
}